import { ReactNode, useEffect, useRef, useState } from 'react'
import { Box, Fade, IconButton, Typography, alpha } from '@mui/material'
import { ZoomIn, ZoomOut, Close } from '@mui/icons-material'
import {
  TransformWrapper,
  TransformComponent,
  useControls
} from 'react-zoom-pan-pinch'
import getSteppedZoom from './getSteppedZoom'
import useSteppedWheelZoom from './useSteppedWheelZoom'

interface IZoomableImage {
  image: string
  alt: string
  minZoom?: number
  maxZoom?: number
  zoomStep?: number
  onZoomChange?: (zoomLevel: number) => void
  /** Camada absoluta sobre a imagem (mesmo box), que acompanha o zoom e o pan. */
  overlay?: ReactNode
}

interface IZoomControls {
  zoom: number
  isZoomedIn: boolean
  minZoom: number
  maxZoom: number
  zoomStep: number
  onWheelWithoutModifier: () => void
}

const WHEEL_HINT_DURATION_MS = 1500

const controlButtonSx = {
  color: 'black',
  '&:hover': {
    bgcolor: 'rgba(255, 255, 255, 0.2)'
  },
  '&:disabled': {
    color: 'rgba(0, 0, 0, 0.5)'
  }
}

const ZoomControls: React.FC<IZoomControls> = ({
  zoom,
  isZoomedIn,
  minZoom,
  maxZoom,
  zoomStep,
  onWheelWithoutModifier
}) => {
  const { instance, zoomIn, zoomOut, resetTransform } = useControls()
  useSteppedWheelZoom({
    step: zoomStep,
    minZoom,
    maxZoom,
    onWheelWithoutModifier
  })

  const handleZoomIn = () => {
    const { scale } = instance.state
    void zoomIn(getSteppedZoom(scale, 'in', zoomStep, minZoom, maxZoom) - scale)
  }

  const handleZoomOut = () => {
    const { scale } = instance.state
    void zoomOut(
      scale - getSteppedZoom(scale, 'out', zoomStep, minZoom, maxZoom)
    )
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        display: 'flex',
        gap: 0.5,
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        borderRadius: 1,
        padding: '4px',
        zIndex: 5
      }}
    >
      <IconButton
        onClick={handleZoomOut}
        disabled={!isZoomedIn}
        size='small'
        sx={controlButtonSx}
        aria-label='Diminuir zoom'
      >
        <ZoomOut fontSize='small' />
      </IconButton>
      <Typography
        sx={{
          minWidth: '40px',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          color: 'black',
          fontSize: '0.875rem',
          fontWeight: 500
        }}
      >
        {Math.round(zoom * 100)}%
      </Typography>
      <IconButton
        onClick={handleZoomIn}
        disabled={zoom >= maxZoom}
        size='small'
        sx={controlButtonSx}
        aria-label='Aumentar zoom'
      >
        <ZoomIn fontSize='small' />
      </IconButton>
      {isZoomedIn && (
        <IconButton
          onClick={() => resetTransform()}
          size='small'
          sx={{
            ...controlButtonSx,
            borderLeft: '1px solid rgba(0, 0, 0, 0.2)',
            borderRadius: 0
          }}
          title='Resetar zoom'
          aria-label='Resetar zoom'
        >
          <Close fontSize='small' />
        </IconButton>
      )}
    </Box>
  )
}

const ZoomableImage: React.FC<IZoomableImage> = ({
  image,
  alt,
  minZoom = 1,
  maxZoom = 4,
  zoomStep = 0.5,
  onZoomChange,
  overlay
}) => {
  const [zoom, setZoom] = useState(minZoom)
  const [showWheelHint, setShowWheelHint] = useState(false)
  const wheelHintTimeout = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(wheelHintTimeout.current), [])

  const showWheelHintBriefly = () => {
    setShowWheelHint(true)
    clearTimeout(wheelHintTimeout.current)
    wheelHintTimeout.current = setTimeout(
      () => setShowWheelHint(false),
      WHEEL_HINT_DURATION_MS
    )
  }

  const isZoomedIn = zoom > minZoom

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: isZoomedIn ? 'grab' : 'default',
        '&:active': { cursor: isZoomedIn ? 'grabbing' : 'default' }
      }}
    >
      <TransformWrapper
        minScale={minZoom}
        maxScale={maxZoom}
        initialScale={minZoom}
        limitToBounds
        centerOnInit
        centerZoomedOut
        wheel={{ disabled: true }}
        // Em 100% não há o que deslocar; sem isso o toque de um dedo não rola a página.
        panning={{ disabled: !isZoomedIn }}
        doubleClick={{ mode: 'toggle', step: zoomStep * 2 }}
        onTransform={(_, state) => {
          // Arredonda para evitar ruído de ponto flutuante durante a animação
          const scale = Math.round(state.scale * 100) / 100
          if (scale === zoom) return
          setZoom(scale)
          onZoomChange?.(scale)
        }}
      >
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{
            width: '100%',
            height: '100%',
            position: 'relative'
          }}
        >
          <Box
            component='img'
            src={image}
            alt={alt}
            draggable={false}
            sx={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              userSelect: 'none'
            }}
          />
          {overlay}
        </TransformComponent>
        <ZoomControls
          zoom={zoom}
          isZoomedIn={isZoomedIn}
          minZoom={minZoom}
          maxZoom={maxZoom}
          zoomStep={zoomStep}
          onWheelWithoutModifier={showWheelHintBriefly}
        />
      </TransformWrapper>
      <Box
        role='status'
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 6
        }}
      >
        <Fade in={showWheelHint} unmountOnExit>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme => alpha(theme.palette.common.black, 0.4)
            }}
          >
            <Typography
              sx={{
                px: 2,
                py: 1,
                borderRadius: 1,
                color: 'common.white',
                bgcolor: theme => alpha(theme.palette.common.black, 0.7)
              }}
            >
              Use Ctrl + roda do mouse para dar zoom
            </Typography>
          </Box>
        </Fade>
      </Box>
    </Box>
  )
}

export default ZoomableImage
